import Button from '@amzn/meridian/button';
import { rio } from '@amzn/rio-design-tokens';
import styled from '@emotion/styled';
const BaseButton = styled(Button) `
  height: ${({ height }) => (height === 'small' ? '34px' : '44px')};

  // Remove transition set by Meridian
  transition: none;

  // Fix button highlighting issue in chrome mobile viewpoint
  // https://stackoverflow.com/questions/45049873/how-to-remove-the-blue-highlight-of-button-on-mobile?rq=3
  -webkit-tap-highlight-color: transparent;

  border-radius: ${({ shape }) => rio.semantic.size.radius[shape || 'full']};

  // Overwrite Meridian's focus ring
  &:after {
    border: 2px solid ${({ theme }) => theme.rio.semantic.color.interaction.primary};
    border-radius: ${({ shape }) => rio.semantic.size.radius[shape || 'full']};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.rio.semantic.color.background.primary};
  }
`;
export const RegularButton = styled(BaseButton) `
  background-color: ${({ theme }) => theme.rio.semantic.color.background.primary};
  color: ${({ theme }) => theme.rio.semantic.color.font.primary};
  border: 1px solid ${({ theme }) => theme.rio.base.color.gray[theme.mode === 'light' ? 700 : 1000]};

  &:hover {
    background-color: ${({ theme }) => theme.rio.semantic.color.background.primary};
    color: ${({ theme }) => theme.rio.semantic.color.font.primary};
    border: 1px solid ${({ theme }) => theme.rio.base.color.gray[theme.mode === 'light' ? 700 : 1000]};
    box-shadow: none;
  }

  &:active {
    background-color: ${({ theme }) => theme.rio.semantic.color.background.active};
    color: ${({ theme }) => theme.rio.semantic.color.font.primary};
    border: 1px solid ${({ theme }) => theme.rio.base.color.blue[theme.mode === 'light' ? 55 : 45]};
  }

  &:disabled {
    border: 1px solid ${({ theme }) => theme.rio.semantic.color.border.disabled};
    background-color: ${({ theme }) => theme.rio.semantic.color.background.disabled};
    color: ${({ theme }) => theme.rio.semantic.color.font.disabled};
  }
`;
export const InverseButton = styled(BaseButton) `
  background-color: ${({ theme }) => theme.rio.semantic.color.background.inverse};
  color: ${({ theme }) => theme.rio.semantic.color.font.inverse};
  border: 1px solid ${({ theme }) => theme.rio.base.color.gray[theme.mode === 'dark' ? 700 : 1000]};

  &:hover {
    background-color: ${({ theme }) => theme.rio.semantic.color.background.inverse};
    color: ${({ theme }) => theme.rio.semantic.color.font.inverse};
    border: 1px solid ${({ theme }) => theme.rio.base.color.gray[theme.mode === 'dark' ? 700 : 1000]};
    box-shadow: none;
  }

  &:active {
    background-color: ${({ theme }) => theme.rio.base.color.blue[theme.mode === 'dark' ? 97 : 20]};
    color: ${({ theme }) => theme.rio.semantic.color.font.inverse};
    border: 1px solid ${({ theme }) => theme.rio.base.color.blue[theme.mode === 'dark' ? 55 : 45]};
  }

  &:disabled {
    border: 1px solid ${({ theme }) => theme.rio.base.color.gray[theme.mode === 'dark' ? 1200 : 400]};
    background-color: ${({ theme }) => theme.rio.base.color.gray[theme.mode === 'dark' ? 1400 : 200]};
    color: ${({ theme }) => theme.rio.base.color.gray[theme.mode === 'dark' ? 500 : 900]};
  }
`;
export const YellowButton = styled(BaseButton) `
  background-color: ${rio.semantic.color.primaryAction.primary};
  color: ${rio.semantic.color.font.primary};

  &:hover {
    background-color: ${rio.semantic.color.primaryAction.primary};
    color: ${rio.semantic.color.font.primary};
    box-shadow: none;
  }

  &:active {
    background-color: ${rio.base.color.yellow[500]};
    color: ${rio.semantic.color.font.primary};
  }

  &:disabled {
    border: 1px solid ${rio.base.color.yellow[1200]};
    background-color: ${rio.base.color.yellow[1500]};
    color: ${rio.semantic.color.font.disabled};
  }
`;
export const OrangeButton = styled(BaseButton) `
  background-color: ${rio.semantic.color.buyNow.primary};
  color: ${rio.semantic.color.font.primary};

  &:hover {
    background-color: ${rio.semantic.color.buyNow.primary};
    color: ${rio.semantic.color.font.primary};
    box-shadow: none;
  }

  &:active {
    background-color: ${rio.base.color.orange[300]};
    color: ${rio.semantic.color.font.primary};
  }

  &:disabled {
    border: 1px solid ${rio.base.color.orange[1200]};
    background-color: ${rio.base.color.orange[1500]};
    color: ${rio.semantic.color.font.disabled};
  }
`;
export const PrimeButton = styled(BaseButton) `
  background-color: ${rio.semantic.color.retail.prime};
  color: ${rio.semantic.color.font.inverse};

  &:hover {
    background-color: #0578ff;
    color: ${rio.semantic.color.font.inverse};
    box-shadow: none;
  }

  &:active {
    background-color: ${rio.base.color.blue[50]};
    color: ${rio.semantic.color.font.inverse};
  }

  &:disabled {
    border: 1px solid ${rio.base.color.blue[80]};
    background-color: ${rio.base.color.blue[97]};
    color: ${rio.semantic.color.font.disabled};
  }
`;
export const TertiaryButton = styled(BaseButton) `
  &,
  &:hover {
    background-color: ${({ theme }) => theme.rio.semantic.color.background.secondary};
    color: ${({ theme }) => theme.rio.semantic.color.link.default};
    box-shadow: none;
  }

  &:focus {
    box-shadow:
      0 0 0 2px ${({ theme }) => theme.rio.semantic.color.background.primary},
      0 0 0 5px ${({ theme }) => theme.rio.semantic.color.border.selected};
  }

  &:active {
    background-color: ${({ theme }) => theme.rio.semantic.color.background.hover};
    color: ${({ theme }) => rio.base.color.blue[theme.mode === 'light' ? 20 : 80]};
  }

  &:disabled {
    background-color: ${({ theme }) => rio.base.color.gray[theme.mode === 'light' ? 1400 : 300]};
    color: ${({ theme }) => rio.base.color.gray[theme.mode === 'light' ? 500 : 1200]};
  }
`;
export const TwisterButton = styled(BaseButton) `
  &,
  &:active,
  &:hover,
  &:focus {
    background-color: ${({ theme, isActive }) => theme.rio.semantic.color.background[isActive ? 'inverse' : 'primary']};
    color: ${({ theme, isActive }) => theme.rio.semantic.color.font[isActive ? 'inverse' : 'primary']};
    border: 1px solid
      ${({ theme, isActive }) => {
    const unselectedBorder = theme.mode === 'dark' ? 1000 : 700;
    const selectedBorder = theme.mode === 'dark' ? 700 : 1000;
    return theme.rio.base.color.gray[isActive ? selectedBorder : unselectedBorder];
}};

    ${({ theme, isDisabled }) => isDisabled &&
    `
      border-style: dashed;
      background: ${theme.rio.semantic.color.background.disabled};
      color: ${theme.rio.semantic.color.font.disabled};
    `}
  }
`;
export const PillButton = styled(BaseButton) `
  &,
  &:active,
  &:hover,
  &:focus {
    height: 32px;
    padding: 6px 12px;
    border-radius: ${rio.semantic.size.radius.extraLarge};
    background: ${({ theme, alexaVariant }) => {
    if (alexaVariant) {
        // https://www.figma.com/design/ex0GjPQoDVt2wT3Ek3NAeG/Blue-Heron-CXs-for-4-15?node-id=11688-29781&t=KNVZYNOptfxi1lDz-0
        return '#C2DCFF';
    }
    return theme.mode === 'dark'
        ? 'linear-gradient(91.07deg, #3555FF 26.92%, #4800BE 100%)'
        : rio.base.color.blue[95];
}};
    color: ${({ theme, alexaVariant }) => {
    if (alexaVariant) {
        // https://www.figma.com/design/ex0GjPQoDVt2wT3Ek3NAeG/Blue-Heron-CXs-for-4-15?node-id=11688-29781&t=KNVZYNOptfxi1lDz-0
        return '#002D74';
    }
    return theme.mode === 'dark' ? rio.semantic.color.background.primary : rio.semantic.color.link.default;
}};
    user-select: none;
    border: none;
    outline: none;
    -webkit-tap-highlight-color: transparent;
  }
`;
/**
 * InvisibleButton - A button with all default browser styles removed.
 * Use this as a base for custom button implementations that need full styling control.
 */
export const InvisibleButton = styled.button `
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;
